export const PARTNER_ORGANIZATION_MAX_DEPTH = 3;

export const PARTNER_ORGANIZATION_DEPTH_ERROR =
  "אפשר עד שלוש רמות של ארגונים";
export const PARTNER_ORGANIZATION_CYCLE_ERROR =
  "לא ניתן ליצור מעגל בין ארגונים";
export const PARTNER_ORGANIZATION_SELF_PARENT_ERROR =
  "לא ניתן לשייך ארגון לעצמו";
export const PARTNER_ORGANIZATION_PARENT_MISSING_ERROR = "ארגון האב לא נמצא";
export const PARTNER_ORGANIZATION_HAS_CHILDREN_ERROR =
  "לא ניתן למחוק ארגון כל עוד יש לו תתי ארגונים";

export type PartnerOrgLink = {
  id: string;
  parentId: string | null;
};

/** Self, direct children, and grandchildren. Depth is capped at 3. */
export function partnerOrganizationSubtreeFilter(orgId: string) {
  return {
    OR: [
      { id: orgId },
      { parentId: orgId },
      { parent: { parentId: orgId } },
    ],
  };
}

function parentIdOf(
  rows: PartnerOrgLink[],
  id: string,
): string | null | undefined {
  const row = rows.find((item) => item.id === id);
  if (!row) return undefined;
  return row.parentId;
}

function childIdsOf(rows: PartnerOrgLink[], id: string): string[] {
  return rows.filter((item) => item.parentId === id).map((item) => item.id);
}

/** The parent, then its parent, up to the root. */
export function partnerOrganizationParentChain(
  parentId: string,
  rows: PartnerOrgLink[],
): string[] {
  const chain: string[] = [];
  const seen = new Set<string>();
  let current: string | null = parentId;
  while (current) {
    if (seen.has(current)) {
      throw new Error(PARTNER_ORGANIZATION_CYCLE_ERROR);
    }
    seen.add(current);
    const next = parentIdOf(rows, current);
    if (next === undefined) {
      throw new Error(PARTNER_ORGANIZATION_PARENT_MISSING_ERROR);
    }
    chain.push(current);
    current = next;
  }
  return chain;
}

/** 1 for a leaf, 2 when it has a child, 3 when it has a grandchild. */
export function partnerOrganizationSubtreeHeight(
  orgId: string,
  rows: PartnerOrgLink[],
): number {
  const seen = new Set<string>();
  const walk = (id: string): number => {
    if (seen.has(id)) {
      throw new Error(PARTNER_ORGANIZATION_CYCLE_ERROR);
    }
    seen.add(id);
    const children = childIdsOf(rows, id);
    if (children.length === 0) return 1;
    return 1 + Math.max(...children.map(walk));
  };
  return walk(orgId);
}

export function assertPartnerOrganizationParentChange(input: {
  orgId: string | null;
  parentId: string | null;
  rows: PartnerOrgLink[];
}): void {
  const parentId = input.parentId;
  if (!parentId) return;
  if (input.orgId && parentId === input.orgId) {
    throw new Error(PARTNER_ORGANIZATION_SELF_PARENT_ERROR);
  }

  const chain = partnerOrganizationParentChain(parentId, input.rows);
  if (input.orgId && chain.includes(input.orgId)) {
    throw new Error(PARTNER_ORGANIZATION_CYCLE_ERROR);
  }

  const height = input.orgId
    ? partnerOrganizationSubtreeHeight(input.orgId, input.rows)
    : 1;
  if (chain.length + height > PARTNER_ORGANIZATION_MAX_DEPTH) {
    throw new Error(PARTNER_ORGANIZATION_DEPTH_ERROR);
  }
}

/**
 * Delete leaves first so a parent is removed only after its selected children.
 * Each inner array can be deleted together. A cycle in the selection throws.
 */
export function partnerOrganizationDeleteWaves(
  rows: PartnerOrgLink[],
): string[][] {
  const remaining = new Map(rows.map((row) => [row.id, row.parentId]));
  const waves: string[][] = [];
  while (remaining.size > 0) {
    const leaves = [...remaining.keys()].filter(
      (id) =>
        ![...remaining.values()].some((parentId) => parentId === id),
    );
    if (leaves.length === 0) {
      throw new Error(PARTNER_ORGANIZATION_CYCLE_ERROR);
    }
    waves.push(leaves);
    for (const id of leaves) remaining.delete(id);
  }
  return waves;
}

export type PartnerOrgParentChoice = PartnerOrgLink & {
  disabledReason: string | null;
};

/** Every other organization, including ones that cannot be a parent, with the reason. */
export function partnerOrganizationParentChoices(
  orgId: string | null,
  rows: PartnerOrgLink[],
): PartnerOrgParentChoice[] {
  return rows
    .filter((row) => !orgId || row.id !== orgId)
    .map((row) => {
      try {
        assertPartnerOrganizationParentChange({
          orgId,
          parentId: row.id,
          rows,
        });
        return { ...row, disabledReason: null };
      } catch (error) {
        return {
          ...row,
          disabledReason:
            error instanceof Error
              ? error.message
              : PARTNER_ORGANIZATION_DEPTH_ERROR,
        };
      }
    });
}

export function eligiblePartnerOrganizationParents(
  orgId: string | null,
  rows: PartnerOrgLink[],
): PartnerOrgLink[] {
  return partnerOrganizationParentChoices(orgId, rows)
    .filter((row) => row.disabledReason == null)
    .map(({ disabledReason: _disabledReason, ...row }) => row);
}
